/* eslint-disable react-refresh/only-export-components */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { billingApi, type BillingSummary, type SubscriptionPlan } from '../api';
import { useAuth } from './AuthContext';
import { useFeatureFlag } from './FeatureFlagsContext';

// The rollout flag that gates the entire Gooey+ surface. Kept here so the web
// references the key in one place (the API owns the catalog).
export const GOOEY_PLUS_FLAG = 'gooey_plus';

interface BillingContextValue {
  // Whether the Gooey+ surface is visible at all (the rollout flag is on).
  flagEnabled: boolean;
  // Whether Stripe is configured server-side (false in dev with blank keys).
  configured: boolean;
  // Whether the household currently has premium access (trial or paid).
  entitled: boolean;
  // Whether that access is an admin-granted comp (no Stripe billing to manage).
  comp: boolean;
  status: string | null;
  plan: SubscriptionPlan | null;
  trialEnd: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  // Refetch repeatedly until the household reads as entitled (or attempts run
  // out). Used after returning from Stripe Checkout, where the confirming
  // webhook can land a beat after the success redirect.
  refreshUntilEntitled: (attempts?: number, delayMs?: number) => Promise<void>;
}

const DEFAULT: BillingContextValue = {
  flagEnabled: false,
  configured: false,
  entitled: false,
  comp: false,
  status: null,
  plan: null,
  trialEnd: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  loading: false,
  refresh: async () => {},
  refreshUntilEntitled: async () => {},
};

const BillingContext = createContext<BillingContextValue | undefined>(undefined);

// Holds the signed-in household's Gooey+ subscription state. Fetches the summary
// only when the gooey_plus rollout flag is on AND the user is authenticated, so
// nothing about billing is requested for users without the flag.
export function BillingProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const flagEnabled = useFeatureFlag(GOOEY_PLUS_FLAG);
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const active = isAuthenticated && flagEnabled;

  const refresh = useCallback(async () => {
    if (!active) {
      setSummary(null);
      return;
    }
    try {
      setSummary(await billingApi.getSubscription());
    } catch {
      setSummary(null);
    }
  }, [active]);

  // Poll the summary until it reads as entitled, so the UI flips to "You're on
  // Gooey+" without a manual reload when the checkout webhook arrives slightly
  // after Stripe's success redirect. Each attempt updates state on success, so
  // the page reflects entitlement the moment it's recorded.
  const refreshUntilEntitled = useCallback(
    async (attempts = 6, delayMs = 1500) => {
      if (!active) return;
      for (let i = 0; i < attempts; i += 1) {
        try {
          const next = await billingApi.getSubscription();
          setSummary(next);
          if (next.entitled) return;
        } catch {
          // Transient failure — keep trying until attempts run out.
        }
        if (i < attempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }
    },
    [active],
  );

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const value = useMemo<BillingContextValue>(
    () => ({
      flagEnabled,
      configured: summary?.configured ?? false,
      entitled: summary?.entitled ?? false,
      comp: summary?.comp ?? false,
      status: summary?.status ?? null,
      plan: summary?.plan ?? null,
      trialEnd: summary?.trialEnd ?? null,
      currentPeriodEnd: summary?.currentPeriodEnd ?? null,
      cancelAtPeriodEnd: summary?.cancelAtPeriodEnd ?? false,
      loading,
      refresh,
      refreshUntilEntitled,
    }),
    [flagEnabled, summary, loading, refresh, refreshUntilEntitled],
  );

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

// Tolerant by design: returns inert defaults when no provider is mounted, so a
// component using it in isolation (e.g. a unit test) renders the "no premium"
// path rather than throwing.
export function useBilling(): BillingContextValue {
  return useContext(BillingContext) ?? DEFAULT;
}
